/**
 * Configuration — Appeals save and validation workflow.
 */

import {
  PLATFORM_APPEAL_OUTCOMES,
  PLATFORM_APPEAL_WINDOW,
  PLATFORM_DEPENDENCIES,
  PLATFORM_REJECTED_BEHAVIOUR,
  SOURCE_MODULE_OPTIONS,
  findDuplicateAppealRule,
  findOverlappingAppealRule,
  getApprovedActionOptions,
  getDecisionDefinition,
  getDependencyAvailability,
  nextAppealRuleId,
  normalizeAccountTypes,
  normalizeAppealForm,
} from './appealsConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

export function appealRuleActor() {
  return { ...ACTOR };
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (!key) return;
  if (!result.errors[key]) result.errors[key] = message;
  else if (!String(result.errors[key]).includes(message)) result.errors[key] = `${result.errors[key]} ${message}`;
}

export function collectAppealSaveErrors(formData, allRules, excludeId, { originalLastUpdated, originalId } = {}) {
  const form = normalizeAppealForm(formData);
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeAppealForm);
  const accountTypes = normalizeAccountTypes(form.accountTypes);
  const decision = getDecisionDefinition(form.sourceModule, form.decisionType);

  if (excludeId && originalId && form.id && form.id !== originalId) {
    addError(result, 'id', 'Rule ID cannot be changed.');
  }

  if (!form.sourceModule) addError(result, 'sourceModule', 'Select a Source Module.');
  else if (!SOURCE_MODULE_OPTIONS.some((option) => option.value === form.sourceModule)) {
    addError(result, 'sourceModule', 'Select an available Source Module.');
  }

  if (!form.decisionType) addError(result, 'decisionType', 'Select a Decision Type.');
  else if (!decision) {
    addError(result, 'decisionType', 'This Decision Type is not available for the selected Source Module.');
  } else {
    if (decision.available === false) {
      addError(result, 'decisionType', 'The selected Decision Type is unavailable.');
    }
    if (decision.eligible === false) {
      addError(result, 'decisionType', 'This Decision Type is not available for the selected Source Module.');
    }
    if (decision.complete === false || !decision.sourceModule || !decision.label) {
      addError(result, 'decisionType', 'The selected Decision Type is unavailable.');
    }
    if (decision.sourceModule && form.sourceModule && decision.sourceModule !== form.sourceModule) {
      addError(result, 'decisionType', 'This Decision Type is not available for the selected Source Module.');
    }
    if (decision.windowSupport === false) {
      addError(
        result,
        'decisionType',
        'This Decision Type does not provide the information required for the Appeal Window.'
      );
    }
  }

  if (!accountTypes.length) addError(result, 'accountTypes', 'Select at least one Account Type.');
  else if (decision) {
    const supported = decision.accountTypes || [];
    if (accountTypes.some((type) => !supported.includes(type))) {
      addError(result, 'accountTypes', 'This Decision Type does not support the selected Account Type.');
    }
  }

  const actionOptions = getApprovedActionOptions(form.sourceModule, form.decisionType, accountTypes);
  if (!form.approvedCorrectiveAction) {
    addError(result, 'approvedCorrectiveAction', 'Select an Approved Corrective Action.');
  } else if (!actionOptions.some((option) => option.value === form.approvedCorrectiveAction)) {
    addError(
      result,
      'approvedCorrectiveAction',
      'The selected corrective action is not supported for this scope.'
    );
  } else if (
    actionOptions.find((option) => option.value === form.approvedCorrectiveAction)?.available === false
  ) {
    addError(result, 'approvedCorrectiveAction', 'The selected Approved Corrective Action is unavailable.');
  }

  if (!form.appealAvailability) {
    addError(result, 'appealAvailability', 'Select Enabled or Disabled.');
  } else if (form.appealAvailability !== 'enabled' && form.appealAvailability !== 'disabled') {
    addError(result, 'appealAvailability', 'Select Enabled or Disabled.');
  }

  if ((form.conditions || []).length) {
    addError(result, 'conditions', 'Conditions are not supported for this Appeal Rule.');
  }

  if (form.appealAvailability === 'enabled') {
    const availability = getDependencyAvailability(form.platformDependencies);
    if (!availability.appealSubmissionForm) {
      addError(result, 'dependencies', 'Appeal Submission Form is unavailable.');
    }
    if (!availability.appealReplyForm) {
      addError(result, 'dependencies', 'Appeal Reply Form is unavailable.');
    }
    if (!availability.appealDecisionForm) {
      addError(result, 'dependencies', 'Appeal Decision Form is unavailable.');
    }
    if (!availability.formInventoryValidation) {
      addError(result, 'dependencies', 'Review the Appeal form and its validation requirements.');
    }
    if (!availability.sharedMessageEditor) {
      addError(result, 'dependencies', 'Shared Message Editor is unavailable.');
    }
  }

  const duplicate = findDuplicateAppealRule(others, form);
  if (duplicate) addError(result, 'duplicate', `This Rule duplicates ${duplicate.id}.`);

  const overlap = findOverlappingAppealRule(others, form);
  if (overlap && overlap.id !== duplicate?.id) {
    addError(
      result,
      'overlap',
      `This Rule overlaps ${overlap.id} for the same Source Module, Decision Type and Account Type.`
    );
  }

  if (excludeId) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (!current) {
      addError(result, 'concurrent', 'This Rule is unavailable. Reload the Rule list.');
    } else if (
      originalLastUpdated &&
      (current.lastUpdated || current.modifiedOn) &&
      (current.lastUpdated || current.modifiedOn) !== originalLastUpdated
    ) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved values before saving.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = normalizeAppealForm(formData);
  return {
    sourceModule: form.sourceModule,
    decisionType: form.decisionType,
    accountTypes: normalizeAccountTypes(form.accountTypes),
    approvedCorrectiveAction: form.approvedCorrectiveAction,
    appealAvailability: form.appealAvailability,
    conditions: [],
    appealWindow: PLATFORM_APPEAL_WINDOW,
    rejectedBehaviour: PLATFORM_REJECTED_BEHAVIOUR,
    appealOutcomes: [...PLATFORM_APPEAL_OUTCOMES],
    platformDependencies: form.platformDependencies || { ...PLATFORM_DEPENDENCIES },
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeAppealForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeAppealForm(rule);
}

export function appendTestHistory(rule, summary, passed) {
  const actor = appealRuleActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const id = payload.id || publishedBaseline?.id || nextAppealRuleId(allRules);
  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || publishedBaseline.createdBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    configurationHistory: payload.configurationHistory || publishedBaseline?.configurationHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Appeals',
      sourceModuleRef: 'configuration_appeals',
      changedItem: record.decisionType || record.ruleName || record.name || id,
      itemId: id,
      changeReason: payload.changeReason || record.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: [
        'sourceModule',
        'decisionType',
        'accountTypes',
        'approvedCorrectiveAction',
        'appealAvailability',
        'conditions',
      ],
    });
  } catch {
    /* keep source save; caller surfaces validation separately */
  }
  if (isNew) return [...(allRules || []).filter((rule) => rule.id !== record.id), record];
  return (allRules || []).map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule));
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  return mergeRulesAfterSave(allRules, payload, publishedBaseline);
}

export function collectSaveErrors(formData, allRules, excludeId, options) {
  return collectAppealSaveErrors(formData, allRules, excludeId, options).errors;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectAppealSaveErrors(formData, allRules, excludeId).summary;
}

export function getPublishedConfiguration(row) {
  return getPublishedRule(row);
}

export function configurationSnapshotFromForm(formData) {
  return normalizeAppealForm(formData);
}

export function mergeConfigurationsAfterCreate(allConfigurations, payload, actorLabel = ACTOR.name) {
  const validation = collectAppealSaveErrors(payload, allConfigurations);
  if (validation.summary.length) {
    return { success: false, errors: validation.summary, configurations: allConfigurations };
  }
  const nowIso = new Date().toISOString();
  const record = {
    ...ruleSnapshotFromForm(payload),
    id: payload.id || nextAppealRuleId(allConfigurations),
    dateAdded: nowIso,
    addedBy: actorLabel,
    lastUpdated: nowIso,
    lastUpdatedBy: actorLabel,
    createdBy: actorLabel,
    modifiedBy: actorLabel,
  };
  return { success: true, configurations: [...allConfigurations, record], record };
}

export function mergeConfigurationsAfterUpdate(allConfigurations, payload, baseline, actorLabel = ACTOR.name) {
  const prev = getPublishedRule(baseline) || {};
  const validation = collectAppealSaveErrors(payload, allConfigurations, prev.id, {
    originalLastUpdated: prev.lastUpdated,
    originalId: prev.id,
  });
  if (validation.summary.length) {
    return { success: false, errors: validation.summary, configurations: allConfigurations };
  }
  const nowIso = new Date().toISOString();
  const record = {
    ...prev,
    ...ruleSnapshotFromForm(payload),
    id: prev.id,
    dateAdded: prev.dateAdded,
    addedBy: prev.addedBy || prev.createdBy || actorLabel,
    lastUpdated: nowIso,
    lastUpdatedBy: actorLabel,
    modifiedBy: actorLabel,
  };
  return {
    success: true,
    configurations: allConfigurations.map((row) => (row.id === record.id ? record : row)),
    record,
  };
}

/** @deprecated Appeal Availability is saved with the Rule; there are no Activate actions. */
export function mergeConfigurationsAfterActivate(allConfigurations, configId, actorLabel = ACTOR.name) {
  const row = allConfigurations.find((rule) => rule.id === configId);
  if (!row) return { success: false, message: 'Configuration not found.', configurations: allConfigurations };
  const payload = { ...normalizeAppealForm(row), appealAvailability: 'enabled', lastUpdatedBy: actorLabel };
  return {
    success: true,
    configurations: mergeRulesAfterSave(allConfigurations, payload, row),
    message: 'Appeal Rule saved.',
  };
}

/** @deprecated */
export function mergeConfigurationsAfterDeactivate(allConfigurations, configId, actorLabel = ACTOR.name) {
  const row = allConfigurations.find((rule) => rule.id === configId);
  if (!row) return { success: false, message: 'Configuration not found.', configurations: allConfigurations };
  const payload = { ...normalizeAppealForm(row), appealAvailability: 'disabled', lastUpdatedBy: actorLabel };
  return {
    success: true,
    configurations: mergeRulesAfterSave(allConfigurations, payload, row),
    message: 'Appeal Rule saved.',
  };
}

export const CONFIRM = {
  createTitle: 'Save',
  createMessage: '',
  createLabel: 'Save',
  saveTitle: 'Save',
  saveMessage: '',
  saveLabel: 'Save',
};
